import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics & Meta Pixel Page View Tracker
 * Automatically tracks page views when route changes
 */
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view with Google Analytics
    if (window.gtag) {
      window.gtag('config', 'G-YP985YYHX3', {
        page_path: location.pathname + location.search,
      });
    }

    // Track page view with Google Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'pageview',
        page: {
          path: location.pathname,
          search: location.search,
          title: document.title,
        },
      });
    }

    // Track page view with Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location]);
};

/**
 * Track custom events across all platforms
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Additional event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  // Google Analytics 4 event tracking
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }

  // Google Tag Manager event tracking
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventParams,
    });
  }

  // Meta Pixel event tracking (map to standard events where possible)
  if (window.fbq) {
    // Map custom events to Meta Pixel standard events
    const metaEventMap = {
      'contact_form_submit': 'Contact',
      'case_study_view': 'ViewContent',
      'language_change': 'CustomizeProduct',
      'external_link_click': 'Lead',
    };

    const metaEvent = metaEventMap[eventName] || eventName;
    window.fbq('track', metaEvent, eventParams);
  }
};

/**
 * Track contact form submissions
 * @param {object} formData - Form data that was submitted
 */
export const trackContactFormSubmission = (formData) => {
  // Track with standard event tracking
  trackEvent('contact_form_submit', {
    event_category: 'engagement',
    event_label: 'contact_form',
    contact_email: formData.email,
    contact_name: formData.name,
  });

  // Additional Meta Pixel specific tracking for leads
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: 'Contact Form',
      content_category: 'Contact',
      status: 'submitted',
    });
  }
};

/**
 * Track case study views
 * @param {string} caseTitle - Title of the case study viewed
 */
export const trackCaseStudyView = (caseTitle) => {
  trackEvent('case_study_view', {
    event_category: 'engagement',
    event_label: 'case_study',
    case_title: caseTitle,
  });
};

/**
 * Track language changes
 * @param {string} language - New language code (en/de)
 */
export const trackLanguageChange = (language) => {
  trackEvent('language_change', {
    event_category: 'engagement',
    event_label: 'language_switch',
    language: language,
  });
};

/**
 * Track external link clicks
 * @param {string} url - External URL clicked
 * @param {string} linkText - Text of the link
 */
export const trackExternalLink = (url, linkText) => {
  trackEvent('external_link_click', {
    event_category: 'engagement',
    event_label: 'external_link',
    link_url: url,
    link_text: linkText,
  });
};
